// utils/relatoriosGlobais.js
import axios from 'axios';
import { API_BASE_URL } from '../../config/ApiConfig';

// Armazenamento global para PDFs (evita garbage collection)
window.pdfStorage = window.pdfStorage || {};

// Função utilitária para validar e abrir PDF
const validateAndOpenPdf = (response, navigate, tituloRelatorio = 'Relatório', forcedFileName = null) => {
  // Verificar status da resposta
  if (response.status !== 200) {
    throw new Error(`Erro no servidor: ${response.status}`);
  }

  // Verificar se há dados na resposta
  if (!response.data || response.data.size === 0) {
    throw new Error('PDF não foi gerado corretamente - arquivo vazio');
  }

  // response.data é um Blob (porque responseType: 'blob')
  const pdfBlob = response.data;
  
  // Criar URL do blob
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  // Extrair o nome do arquivo do header Content-Disposition se disponível
  let nomeArquivo = forcedFileName || tituloRelatorio;
  const contentDisposition = response.headers['content-disposition'];
  if (!forcedFileName && contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      nomeArquivo = filenameMatch[1].replace(/['"]/g, '');
    }
  } else if (!forcedFileName) {
    const timestamp = new Date().toISOString().slice(0, 10);
    nomeArquivo = `${tituloRelatorio}_${timestamp}.pdf`;
  }

  try {
    // Guardar blob em storage global para evitar garbage collection
    const pdfId = `pdf_${Date.now()}_${Math.random()}`;
    window.pdfStorage[pdfId] = pdfBlob;
    
    // Armazenar apenas referência e metadados em sessionStorage
    const dataToStore = {
      title: tituloRelatorio,
      nomeArquivo: nomeArquivo,
      pdfId: pdfId,
      url: pdfUrl
    };
    
    sessionStorage.setItem('pdfViewerData', JSON.stringify(dataToStore));
    
    // Disparar evento para atualizar o PdfViewerModal
    window.dispatchEvent(new CustomEvent('pdfViewerUpdate'));
    
    return true;
  } catch (error) {
    console.error('Erro ao processar PDF:', error);
    throw new Error('Erro ao processar PDF: ' + error.message);
  }
};

// Função para gerar Lista de DFD
export const gerarListaDfd = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/lista_dfd`, {
      params: { cnpj: cnpj, ano: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Lista_DFD', `LISTA_DFD_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Lista de Despesas:', error);
    throw error;
  }
};

// Função para gerar PCA
export const gerarPca = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherPcaPdf`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Plano de Contratacao Anual', `PCA_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Plano de Contratação Anual:', error);
    throw error;
  }
};

// Função para gerar PCA em formato Word (.docx)
export const gerarPcaDocx = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    // Usar endpoint já existente que retorna DOCX (RelatorioController.pcaOriginarioDocx)
    const response = await axios.get(`${API_BASE_URL.RELATORIO}/pcaOriginarioDocx`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    if (!response || !response.data || response.data.size === 0) {
      throw new Error('Documento DOCX não foi gerado corretamente - arquivo vazio');
    }

    // Forçar download do .docx
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const link = document.createElement('a');
    const fileName = `PCA_${cnpj}_${anoBase}.docx`;
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch (error) {
    console.error('Erro ao gerar PCA em Word:', error);
    throw error;
  }
};

// Gerar PCA Originário (Word) - sem Anexo I
export const gerarPcaDocxSemAnexo = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/pcaOriginarioSemAnexoDocx`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    if (!response || !response.data || response.data.size === 0) {
      throw new Error('Documento DOCX não foi gerado corretamente - arquivo vazio');
    }

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const link = document.createElement('a');
    const fileName = `PCA_SEM_ANEXO_${cnpj}_${anoBase}.docx`;
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch (error) {
    console.error('Erro ao gerar PCA (sem Anexo) em Word:', error);
    throw error;
  }
};

// Gerar ANEXO I (Word)
export const gerarPcaAnexoIDocx = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/pcaOriginarioAnexoIDocx`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    if (!response || !response.data || response.data.size === 0) {
      throw new Error('Documento DOCX não foi gerado corretamente - arquivo vazio');
    }

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const link = document.createElement('a');
    const fileName = `PCA_ANEXO_I_${cnpj}_${anoBase}.docx`;
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch (error) {
    console.error('Erro ao gerar Anexo I em Word:', error);
    throw error;
  }
};

// Função para gerar DFD Analítico
export const gerarDfdAnalitico = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/dfd_analitico_pdf`, {
      params: { cnpj: cnpj, ano: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'DFD_Analitico', `DFD_ANALITICO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar DFD Analítico:', error);
    throw error;
  }
};

// Função para gerar DFD Sintético
export const gerarDfdSintetico = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/dfd_sintetico_pdf`, {
      params: { cnpj: cnpj, ano: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'DFD_Sintetico', `DFD_SINTETICO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar DFD Sintético:', error);
    throw error;
  }
};

// Função para gerar DFD Alertas
export const gerarDfdAlerta = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/dfd_alerta_pdf`, {
      params: { cnpj: cnpj, ano: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'DFD_Alerta', `DFD_ALERTA_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar DFD Alertas:', error);
    throw error;
  }
};

// Função para gerar Demonstrativo de Requisitante por Natureza
export const gerarDemonstrativoRequisitanteNatureza = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherRequisitanteNatureza`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demonstrativo Requisitante Natureza', `DEMONSTRATIVO_REQUISITANTE_NATUREZA_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demonstrativo Requisitante Natureza:', error);
    throw error;
  }
};

// Função para gerar Cronograma de Contratação
export const gerarCronogramaContratacao= async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherCronogramaContratacao`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Cronograma de Contratação', `CRONOGRAMA_CONTRATACAO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Cronograma de Contratação:', error);
    throw error;
  }
};

// Função para gerar Cronograma de Contratação
export const gerarContratacaoMes= async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherContratacaoMes`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Contratação por Mês', `CONTRATACAO_MES_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Contratação por Mês:', error);
    throw error;
  }
};
// Função para gerar o novo Demonstrativo das Contratações (endpoint novo no backend)
export const gerarDemonstrativoContratacoes = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherDemonstrativoContratacoes`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demonstrativo_Contratacoes', `DEMONSTRATIVO_CONTRATACOES_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demonstrativo das Contratações:', error);
    throw error;
  }
};

export const gerarDemonstrativoGerencialContratacoes = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherDemonstrativoGerencialContratacoes`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demonstrativo_Gerencial_Contratacoes', `DEMONSTRATIVO_GERENCIAL_CONTRATACOES_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demonstrativo Gerencial das Contratações:', error);
    throw error;
  }
};

// Função para gerar Cronograma Integrado
export const gerarCronogramaIntegrado = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/preencherCronogramaIntegrado`,{
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Cronograma Integrado', `CRONOGRAMA_INTEGRADO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Cronograma Integrado:', error);
    throw error;
  }
};

// Função para gerar Cronograma de Licitação
export const gerarCronogramaLicitacao = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherCronogramaLicitacao`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Cronograma de Licitação', `CRONOGRAMA_LICITACAO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Cronograma de Licitação:', error);
    throw error;
  }
};

// Função para gerar Cronograma de Renovação de Contrato
export const gerarCronogramaRenovacaoContrato = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherCronogramaRenovacaoContrato`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Cronograma de Renovação de Contrato', `CRONOGRAMA_RENOVACAO_CONTRATO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Cronograma de Renovação de Contrato:', error);
    throw error;
  }
};

// Função para gerar Cronograma de Contratação
export const gerarLicitacaoMes= async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.post(`${API_BASE_URL.PCA}/preencherLicitacaoMes`, null, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Licitação por Mês', `LICITACAO_MES_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Licitação por Mês:', error);
    throw error;
  }
};
// Função para gerar Demonstrativo das Contratações por Área Req. e Natureza
export const gerarDemonstrativoReqNatureza = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/demonstrativo_req_natureza`, {
      params: { cnpj: cnpj, ano: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demonstrativo_Req_Natureza', `DEMONSTRATIVO_REQ_NATUREZA_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demonstrativo Req. Natureza:', error);
    throw error;
  }
};

// Função para gerar Relatório Gerencial das Contratações
export const gerarRelatorioGerencial = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/relatorio_gerencial`, {
      params: { cnpj: cnpj, ano: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demonstrativo_Gerencial_Contratacoes', `RELATORIO_GERENCIAL_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Relatório Gerencial das Contratações:', error);
    throw error;
  }
};

// Função para gerar Síntese Executiva do Planejamento
export const gerarSinteseExecutiva = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/sinteseExecutiva`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Sintese_Executiva', `SINTESE_EXECUTIVA_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Síntese Executiva:', error);
    throw error;
  }
};

// Função para gerar Demandas de Maior Impacto
export const gerarDemandasMaiorImpacto = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/demandasMaiorImpacto`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demandas_Maior_Impacto', `DEMANDAS_MAIOR_IMPACTO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demandas de Maior Impacto:', error);
    throw error;
  }
};

// Função para gerar Demandas por Prioridade
export const gerarDemandasPorPrioridade = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/demandasPorPrioridade`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demandas_por_Prioridade', `DEMANDAS_POR_PRIORIDADE_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demandas por Prioridade:', error);
    throw error;
  }
};

// Função para gerar Demandas sem Estimativa
export const gerarDemandasSemEstimativa = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/demandasSemEstimativa`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'Demandas_sem_Estimativa', `DEMANDAS_SEM_ESTIMATIVA_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Demandas sem Estimativa:', error);
    throw error;
  }
};

// Função para gerar PCA Originário
export const gerarPcaOriginario = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/pcaOriginario`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'PCA_Originario', `PCA_ORIGINARIO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar PCA Originário:', error);
    throw error;
  }
};

// Função para gerar PCA Originário mesclado com Anexo I (PDF) e abrir no PdfViewerModal
export const gerarPcaComAnexoMesclado = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/pcaOriginarioMesclado`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'PCA_Originario_Com_Anexo', `PCA_ORIGINARIO_COMPLETO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar PCA Originário mesclado:', error);
    throw error;
  }
};

// Função para gerar PCA Revisado
export const gerarPcaRevisado = async (navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    const response = await axios.get(`${API_BASE_URL.RELATORIO}/pcaRevisado`, {
      params: { cnpj: cnpj, anoBase: anoBase },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    validateAndOpenPdf(response, navigate, 'PCA_Revisado', `PCA_REVISADO_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar PCA Revisado:', error);
    throw error;
  }
};

// Função para gerar Lista de Despesas por Mês
export const gerarListaDespesasPorMes = async (mes, navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    if (!mes || mes < 1 || mes > 12) {
      throw new Error('Mês inválido');
    }

    const response = await axios.get(
      `${API_BASE_URL.RELATORIO}/lista_despesa_mes`,
      {
        params: {
          cnpjAssociacao: cnpj,
          ano: anoBase,
          mes: mes,
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      }
    );

    validateAndOpenPdf(response, navigate, `Lista_Despesas_Mes_${mes}`, `LISTA_DESPESAS_MES_${String(mes).padStart(2, '0')}_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Lista de Despesas por Mês:', error);
    throw error;
  }
};

// Função para gerar Lista de Despesas por Período (Data de Elaboração)
export const gerarListaDespesasPorPeriodo = async (dataInicio, dataFim, navigate = null) => {
  try {
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    if (!token || !cnpj) {
      throw new Error('Token ou CNPJ não encontrados');
    }

    if (!dataInicio || !dataFim) {
      throw new Error('Datas de início e fim são obrigatórias');
    }

    // Validar se data início é menor que data fim
    if (new Date(dataInicio) > new Date(dataFim)) {
      throw new Error('Data de início deve ser menor que data de fim');
    }

    const response = await axios.get(
      `${API_BASE_URL.RELATORIO}/lista_despesa_periodo`,
      {
        params: {
          cnpjAssociacao: cnpj,
          ano: anoBase,
          dataInicio: dataInicio,
          dataFim: dataFim,
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      }
    );

    const titulo = `Lista_Despesas_${dataInicio}_a_${dataFim}`;
    validateAndOpenPdf(response, navigate, titulo, `LISTA_DESPESAS_PERIODO_${dataInicio}_A_${dataFim}_${cnpj}_${anoBase}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao gerar Lista de Despesas por Período:', error);
    // Se o endpoint específico não existir, tentar com endpoint genérico
    if (error.response && error.response.status === 404) {
      console.warn(
        'Endpoint período não encontrado, usando endpoint genérico...'
      );
      try {
        const alternativeResponse = await axios.get(
          `${API_BASE_URL.RELATORIO}/lista_despesa`,
          {
            params: {
              cnpjAssociacao: cnpj,
              ano: anoBase,
              dataInicio: dataInicio,
              dataFim: dataFim,
            },
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          }
        );
          const titulo = `Lista_Despesas_${dataInicio}_a_${dataFim}`;
          validateAndOpenPdf(alternativeResponse, navigate, titulo, `LISTA_DESPESAS_PERIODO_${dataInicio}_A_${dataFim}_${cnpj}_${anoBase}.pdf`);
        return true;
      } catch (altError) {
        console.error('Erro no endpoint alternativo:', altError);
        throw new Error('Relatório por período não está disponível no momento');
      }
    }
    throw error;
  }
};

// Lista de relatórios globais sempre disponíveis
export const relatoriosGlobais = [
  {
    label: 'DFDs',
    icon: 'bi bi-journal-text me-2',
    submenu: [
      {
        label: 'Lista de DFDs',
        icon: 'bi bi-list-ul me-2',
        tooltip: 'Lista resumida de todos os DFDs do ano base.',
        action: gerarListaDfd,
      },
      {
        label: 'DFD Analítico',
        icon: 'bi bi-file-earmark-text me-2',
        tooltip: 'Relatório detalhado com objeto, tipo de demanda e situação da estimativa de cada DFD.',
        action: gerarDfdAnalitico,
      },
      {
        label: 'DFD Sintético',
        icon: 'bi bi-file-earmark-bar-graph me-2',
        tooltip: 'Visão gerencial consolidada por unidade requisitante com quadro-resumo de prioridades e tipos.',
        action: gerarDfdSintetico,
      },
      {
        label: 'DFD Alertas',
        icon: 'bi bi-exclamation-triangle me-2',
        tooltip: 'Relatório de consistência com alertas automáticos de valor zerado, objeto genérico e tipo de demanda.',
        action: gerarDfdAlerta,
      },
    ],
  },
  {
    label: 'PCAs',
    icon: 'bi bi-journal-bookmark-fill me-2',
    submenu: [
      {
        label: 'PCA (modelo legado)',
        icon: 'bi bi-journal-text me-2',
        tooltip: 'Plano de Contratação Anual gerado a partir do modelo de template (.docx) legado.',
        action: gerarPca,
      },
      
      {
        label: 'PCA Originário',
        icon: 'bi bi-file-earmark-pdf me-2',
        tooltip: 'Visualizar PCA Originário já mesclado com o ANEXO I (PDF).',
        action: gerarPcaComAnexoMesclado,
      },
      
      
      {
        label: 'PCA Revisado',
        icon: 'bi bi-file-earmark-diff me-2',
        tooltip: 'Plano de Contratação Anual na versão revisada, com quadro comparativo e histórico de alterações.',
        action: gerarPcaRevisado,
      },
    ],
  },
  {
    label: 'Demonstrativos',
    icon: 'bi bi-bar-chart-line me-2',
    submenu: [
      {
        label: 'Demonstrativo das Contratações',
        icon: 'bi bi-bar-chart-line me-2',
        tooltip: 'Novo demonstrativo (margens dinâmicas)',
        action: gerarDemonstrativoContratacoes,
      },
      {
        label: 'Demonstrativo Gerencial das Contratações',
        icon: 'bi bi-bar-chart-line me-2',
        action: gerarDemonstrativoGerencialContratacoes,
      },
    ],
  },
  {
    label: 'Cronogramas',
    icon: 'bi bi-calendar-range me-2',
    submenu: [
      {
        label: 'Cronograma Integrado',
        icon: 'bi bi-calendar3 me-2',
        tooltip: 'Visão integrada de todos os cronogramas de contratação do ano base.',
        action: gerarCronogramaIntegrado,
      },
      {
        label: 'Cronograma de Licitação',
        icon: 'bi bi-calendar-event me-2',
        tooltip: 'Cronograma das licitações previstas no PCA.',
        action: gerarCronogramaLicitacao,
      },
      {
        label: 'Cronograma de Renovação de Contrato',
        icon: 'bi bi-arrow-repeat me-2',
        tooltip: 'Cronograma das renovações de contratos previstas no PCA.',
        action: gerarCronogramaRenovacaoContrato,
      },
    ],
  },
  {
    label: 'Gerenciais',
    icon: 'bi bi-graph-up me-2',
    submenu: [
      {
        label: 'Síntese Executiva do Planejamento',
        icon: 'bi bi-clipboard-data me-2',
        tooltip: 'Visão executiva consolidada do PCA com ranking por área, distribuição por prioridade e distribuição mensal.',
        action: gerarSinteseExecutiva,
      },
      {
        label: 'Demandas de Maior Impacto',
        icon: 'bi bi-graph-up-arrow me-2',
        tooltip: 'Ranking das demandas com maior valor estimado e análise de concentração financeira.',
        action: gerarDemandasMaiorImpacto,
      },
      {
        label: 'Demandas por Prioridade',
        icon: 'bi bi-sort-down me-2',
        tooltip: 'Consolidação e listagem analítica das demandas agrupadas por nível de prioridade (Alta, Média, Baixa).',
        action: gerarDemandasPorPrioridade,
      },
      {
        label: 'Demandas sem Estimativa',
        icon: 'bi bi-exclamation-circle me-2',
        tooltip: 'Relatório de saneamento com as demandas que possuem valor ou quantidade zerados ou não informados.',
        action: gerarDemandasSemEstimativa,
      },
    ],
  },
  // ── Ocultos até nova decisão ──────────────────────────────────────────────
  {
    hidden: true,
    label: 'Contratação por Mês',
    icon: 'bi bi-calendar-month me-2',
    action: gerarContratacaoMes,
  },
  {
    hidden: true,
    label: 'Licitação por Mês',
    icon: 'bi bi-file-earmark-bar-graph me-2',
    action: gerarLicitacaoMes,
  },
  // Adicione outros relatórios globais aqui
];

// Helper para executar um relatório ou avisar que está em construção.
// Uso: import { triggerReport } from '.../relatoriosGlobais';
// onClick={() => triggerReport(report)}
export const triggerReport = async (report, ...args) => {
  if (!report) return Promise.reject(new Error('Relatório inválido'));

  if (typeof report.action === 'function') {
    try {
      // action pode retornar Promise ou valor direto
      const result = await report.action(...args);
      return result;
    } catch (err) {
      console.error('Erro ao executar action do relatório:', err);
      throw err;
    }
  }

  // Feedback quando não há action definida
  const msg = `${report.label} não está disponível no momento — relatório em construção.`;

  // Se Bootstrap Toast estiver disponível, usar; caso contrário, fallback para alert()
  if (
    typeof window !== 'undefined' &&
    window.bootstrap &&
    typeof window.bootstrap.Toast === 'function'
  ) {
    const toastEl = document.createElement('div');
    toastEl.className = 'toast align-items-center text-bg-warning border-0';
    toastEl.role = 'alert';
    toastEl.ariaLive = 'assertive';
    toastEl.ariaAtomic = 'true';
    toastEl.style.position = 'fixed';
    toastEl.style.top = '1rem';
    toastEl.style.right = '1rem';
    toastEl.style.zIndex = 1080;
    toastEl.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">${msg}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    `;
    document.body.appendChild(toastEl);
    const toast = new window.bootstrap.Toast(toastEl, { delay: 3500 });
    toast.show();
    toastEl.addEventListener('hidden.bs.toast', () => toastEl.remove());
    return Promise.resolve(false);
  }

  // Fallback simples
  // eslint-disable-next-line no-alert
  alert(msg);
  return Promise.resolve(false);
};
